import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

// A long guide page carries the first few rows of each section (ListingHelper). The rest come in one
// request, as the reader nears a section's placeholder, or before a search or a jump to a day (which
// need every row) asks for them with load(). Each section's rows are swapped in whole, and the page is
// kept still while sections above the reader grow.
export default class extends Controller {
  static targets = ["placeholder"]
  static values = { url: String }

  connect() {
    if (!this.pending) return

    this.observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) this.load().catch(() => {})
    }, { rootMargin: "600px 0px" })
    this.placeholderTargets.forEach(placeholder => this.observer.observe(placeholder))
  }

  disconnect() {
    this.observer?.disconnect()
  }

  get pending() {
    return this.hasPlaceholderTarget
  }

  // Resolves once every section is whole
  load() {
    if (!this.pending) return Promise.resolve()

    this.loading ??= this.fetchRows().finally(() => { this.loading = null })
    return this.loading
  }

  // A placeholder's own link: show the rows here, or go to the page that lists them if they can't be fetched
  show(event) {
    event.preventDefault()
    const href = event.currentTarget.href
    this.load().catch(() => Turbo.visit(href))
  }

  async fetchRows() {
    const response = await fetch(this.urlValue, { headers: { Accept: "text/html" } })
    if (!response.ok) throw new Error(`Couldn't load the rest of the guide (${response.status})`)

    const page = new DOMParser().parseFromString(await response.text(), "text/html")
    const anchor = this.anchor()
    const top = anchor?.getBoundingClientRect().top

    page.querySelectorAll("template[data-rows-for]").forEach(template => {
      document.getElementById(template.dataset.rowsFor)?.replaceWith(document.importNode(template.content, true))
    })

    // Browsers that don't anchor scrolling themselves would otherwise jump as the sections above grow
    if (anchor?.isConnected) {
      const moved = anchor.getBoundingClientRect().top - top
      if (Math.abs(moved) >= 1) window.scrollBy(0, moved)
    }
    this.observer?.disconnect()
    this.dispatch("loaded")
  }

  // The section being read: the last one whose top has reached the top of the window
  anchor() {
    const sections = [...this.element.querySelectorAll("[data-day-section]")]
    return sections.filter(section => section.getBoundingClientRect().top <= 0).pop() || sections[0]
  }
}
